import atoms from '@Components/Utilities/SandhillsAtoms';
import { useEffect, useRef, useState } from 'react';
import { useRecoilValue } from 'recoil';

const typingSpeed = 50;
const pauseDuration = 3000;
const cursorBlinkSpeed = 500;

const AnimatedQuickFindLabel = (props) => {
    const keywordSearchState = useRecoilValue(atoms.keywordSearchStateAtom);
    const [animatedTerm, setAnimatedTerm] = useState('');
    const [isAnimating, setIsAnimating] = useState(true);
    const [showCursor, setShowCursor] = useState(true);
    const timeoutRef = useRef(null);
    const cursorBlinkRef = useRef(null);
 
    let currTermIndex = 0;
    let currCharIndex = 0;

    const cursorStyle = {
        opacity: showCursor ? 1 : 0
    };

    useEffect(() => {
        if (props.focused) {
            setIsAnimating(false); 
            stopCursorBlink();

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        }
    }, [props.focused])

    const getAnimatedPlaceholder = () => {
        if (!props.focused && keywordSearchState.SearchText) return '';
        if (props.focused) return '';
        if (!isAnimating) {
            if (props.defaultText) return props.defaultText;
            return keywordSearchState.QuickFindPlaceholder;
        }

        return (
            <>
                <span className='visually-hidden'>{keywordSearchState.QuickFindPlaceholder}</span>
                {animatedTerm}
                <span style={cursorStyle} className="animated-cursor"></span>
            </>
        );
    };

    const getDefaultPlaceholder = () => {
        if (props.focused || keywordSearchState.SearchText) return '';        
        return keywordSearchState.QuickFindPlaceholder;
    };


    const startCursorBlink = () => {
        if (cursorBlinkRef.current) {
            clearInterval(cursorBlinkRef.current);
        }

        cursorBlinkRef.current = setInterval(() => {
            setShowCursor(prev => !prev);
        }, cursorBlinkSpeed);
    };


    const stopCursorBlink = () => {
        if (cursorBlinkRef.current) {
            clearInterval(cursorBlinkRef.current);
            cursorBlinkRef.current = null;
        }
    };

    const typeNextChar = (skipFirst) => {
        if (!isAnimating) return;

        if (currCharIndex < keywordSearchState.AutocompleteTerms[currTermIndex].length) {
            if (!skipFirst) {
                setAnimatedTerm((prev) => prev + keywordSearchState.AutocompleteTerms[currTermIndex][currCharIndex]);
                setShowCursor(true); // Show cursor when typing
            }
            timeoutRef.current = setTimeout(() => typeNextChar(false), typingSpeed);
            currCharIndex = currCharIndex + 1;
        } else {
            timeoutRef.current = setTimeout(deleteText, pauseDuration);
        }
    }

    const deleteText = () => {
        if (!isAnimating) return;

        if (currCharIndex > 0 && currTermIndex + 1 < keywordSearchState.AutocompleteTerms.length) {
            setAnimatedTerm('');
            currCharIndex = 0;
            deleteText();
        }
        else if (currTermIndex + 1 < keywordSearchState.AutocompleteTerms.length) {
            currTermIndex = (currTermIndex + 1) % keywordSearchState.AutocompleteTerms.length;
            timeoutRef.current = setTimeout(() => typeNextChar(false), (typingSpeed * 10));
        }
        else {
            // If we've gone through all terms, start over from the first term
            currTermIndex = 0;
            currCharIndex = 0;
            setAnimatedTerm('');
            timeoutRef.current = setTimeout(() => typeNextChar(false), (typingSpeed * 10));
        }
    }

    const checkScrollPosition = () => {
        const scrollPosition = window.scrollY;
        const isAtTop = scrollPosition < 50;

        if (!isAtTop && isAnimating) {
            setIsAnimating(false);
            stopCursorBlink();

            setAnimatedTerm(keywordSearchState.QuickFindPlaceholder);

            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        }
    };

    useEffect(() => {
        if (keywordSearchState.AutocompleteTerms &&
            keywordSearchState.AutocompleteTerms.length > 0 &&
            isAnimating) {

            // Clear any existing timeouts
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }

            // Reset animation state
            currTermIndex = 0;
            currCharIndex = 0;

            // Start cursor blinking
            startCursorBlink();

            // Start new animation sequence
            setAnimatedTerm(keywordSearchState.AutocompleteTerms[currTermIndex][currCharIndex]);
            timeoutRef.current = setTimeout(() => typeNextChar(true), typingSpeed);
        }

        window.addEventListener('scroll', checkScrollPosition);
        return () => {
            window.removeEventListener('scroll', checkScrollPosition);
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            stopCursorBlink();
        };
    }, [keywordSearchState.AutocompleteTerms, isAnimating]);

    return (
        <>
            {
                keywordSearchState.AutocompleteTerms?.length > 0
                    ? getAnimatedPlaceholder()
                    : getDefaultPlaceholder()
            }
        </>
    );
};

export default AnimatedQuickFindLabel;